import React, { useState, useEffect } from 'react';
import { Settings2, Calendar, Building2, MapPin, Save, ChevronDown, EyeOff, MessageSquare, Edit3 } from 'lucide-react';
import { useApplicationSettingsContext } from '../../contexts/ApplicationSettingsContext';
import { HomeScreenOption, PageId } from '../../hooks/useApplicationSettings';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useNavigate } from 'react-router-dom';
import CustomizeFeedbackFormModal from '../../components/CustomizeFeedbackFormModal';

// Navigation page groups with display names
const navigationPages = {
  topNav: [
    { id: 'calendarV2', label: 'Location Calendar' },
    { id: 'mobile-calendar', label: 'Mobile Calendar' },
    { id: 'bookings', label: 'Bookings' },
    { id: 'proposals', label: 'Proposals' },
    { id: 'customers', label: 'Customers' },
  ],
  sideNav: [
    { id: 'messages', label: 'Messages' },
    { id: 'crm', label: 'CRM' },
    { id: 'payroll', label: 'Payroll' },
    { id: 'timeclock', label: 'Timeclock' },
    { id: 'supplies', label: 'Supplies' },
    { id: 'services', label: 'Services' },
    { id: 'resources', label: 'Resources' },
    { id: 'automations', label: 'Automations' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'dialler', label: 'Dialler' },
    { id: 'sales-reports', label: 'Sales Reports' },
    { id: 'team-reports', label: 'Team Reports' },
    { id: 'supplies-usage', label: 'Supplies Usage' },
  ],
  settingsPages: [
    { id: 'flat-rates', label: 'Flat Rates' },
    { id: 'materials', label: 'Materials' },
    { id: 'operating-costs', label: 'Operating Costs' },
    { id: 'online-bookings', label: 'Online Bookings' },
  ]
};

export default function ApplicationSettings() {
  const { homeScreen, disabledPages, isLoading, updateHomeScreen, updateDisabledPages, refreshSettings } = useApplicationSettingsContext();
  const { selectedBusiness } = useBusiness();
  const [tempHomeScreen, setTempHomeScreen] = useState<HomeScreenOption>(homeScreen);
  const [tempDisabledPages, setTempDisabledPages] = useState<PageId[]>(disabledPages || []);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [tempFeedbackEnabled, setTempFeedbackEnabled] = useState(true);
  const [isLoadingFeedbackSettings, setIsLoadingFeedbackSettings] = useState(true);
  const [isCustomizeFeedbackModalOpen, setIsCustomizeFeedbackModalOpen] = useState(false);
  const navigate = useNavigate();

  // Update tempHomeScreen when homeScreen changes (e.g., after refresh)
  useEffect(() => {
    setTempHomeScreen(homeScreen);
    
    // Ensure the current home screen is not in disabled pages
    if (disabledPages && disabledPages.length > 0) {
      const homeScreenPageId = homeScreen === 'location_calendar' ? 'calendarV2' as PageId : 'mobile-calendar' as PageId;
      
      if (disabledPages.includes(homeScreenPageId)) {
        // Remove it from the disabled pages list
        const updatedDisabledPages = disabledPages.filter(id => id !== homeScreenPageId);
        setTempDisabledPages(updatedDisabledPages);
        
        // Also update it in the database
        updateDisabledPages(updatedDisabledPages).catch(error => {
          console.error('Error updating disabled pages:', error);
        });
      }
    }
  }, [homeScreen, disabledPages, updateDisabledPages]);

  // Update tempDisabledPages when disabledPages changes
  useEffect(() => {
    setTempDisabledPages(disabledPages || []);
  }, [disabledPages]);

  // Load feedback settings when component mounts
  useEffect(() => {
    loadFeedbackSettings();
  }, [selectedBusiness?.id]);

  const handleSelectOption = (option: HomeScreenOption) => {
    setTempHomeScreen(option);
    setIsOpen(false);
    
    // Get the page ID that corresponds to the new home screen
    const homeScreenPageId = option === 'location_calendar' ? 'calendarV2' as PageId : 'mobile-calendar' as PageId;
    
    // If the new home screen page is in the disabled list, remove it
    if (tempDisabledPages.includes(homeScreenPageId)) {
      setTempDisabledPages(prev => prev.filter(id => id !== homeScreenPageId));
    }
  };

  const handleTogglePage = (pageId: PageId) => {
    // Check if this page corresponds to the current home screen
    const isHomeScreen = 
      (tempHomeScreen === 'location_calendar' && pageId === 'calendarV2') || 
      (tempHomeScreen === 'mobile_calendar' && pageId === 'mobile-calendar');
    
    // Check if this is the current Application Settings page
    const isCurrentPage = pageId === 'application-settings';
    
    // Prevent disabling the current home screen or current page
    if (isHomeScreen || isCurrentPage) return;
    
    setTempDisabledPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };

  const hasChanges = () => {
    if (tempHomeScreen !== homeScreen) return true;
    
    // Check if disabled pages have changed
    if (tempDisabledPages.length !== disabledPages.length) return true;
    
    // Check if any page has been added or removed
    const disabledPagesSet = new Set(disabledPages);
    const tempDisabledPagesSet = new Set(tempDisabledPages);
    
    for (const page of disabledPagesSet) {
      if (!tempDisabledPagesSet.has(page)) return true;
    }
    
    for (const page of tempDisabledPagesSet) {
      if (!disabledPagesSet.has(page)) return true;
    }
    
    // Check if feedback settings have changed
    if (tempFeedbackEnabled !== feedbackEnabled) return true;
    
    return false;
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      // Update home screen setting if changed
      if (tempHomeScreen !== homeScreen) {
        await updateHomeScreen(tempHomeScreen);
      }
      
      // Update disabled pages if changed
      if (JSON.stringify(tempDisabledPages) !== JSON.stringify(disabledPages)) {
        await updateDisabledPages(tempDisabledPages);
      }
      
      // Update feedback settings if changed
      if (tempFeedbackEnabled !== feedbackEnabled) {
        const feedbackSaved = await saveFeedbackSettings();
        if (!feedbackSaved) {
          toast.error('Failed to save feedback settings');
          return;
        }
      }
      
      // Refresh settings to ensure latest data
      await refreshSettings();
      
      toast.success(`Settings saved successfully`);
    } catch (error) {
      console.error('Error saving application settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Load feedback settings
  const loadFeedbackSettings = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingFeedbackSettings(true);
    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('feedback_enabled')
        .eq('id', selectedBusiness.id)
        .single();
      
      if (error) {
        console.error('Error loading feedback settings:', error);
        return;
      }
      
      const enabled = data?.feedback_enabled !== false; // Default to true if not set
      setFeedbackEnabled(enabled);
      setTempFeedbackEnabled(enabled);
    } catch (error) {
      console.error('Error loading feedback settings:', error);
    } finally {
      setIsLoadingFeedbackSettings(false);
    }
  };

  // Save feedback settings
  const saveFeedbackSettings = async () => {
    if (!selectedBusiness?.id || !supabase) return false;
    
    try {
      const { error } = await supabase
        .from('businesses')
        .update({ feedback_enabled: tempFeedbackEnabled })
        .eq('id', selectedBusiness.id);
      
      if (error) {
        console.error('Error saving feedback settings:', error);
        return false;
      }
      
      setFeedbackEnabled(tempFeedbackEnabled);
      return true;
    } catch (error) {
      console.error('Error saving feedback settings:', error);
      return false;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center">
            <Settings2 className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Application Settings</h2>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Configure application-wide settings and preferences.
          </p>
        </div>
        
        <div className="p-6">
          <div className="space-y-8">
            {/* Home Screen Setting */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <Calendar className="h-5 w-5 text-gray-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Home Screen</h3>
              </div>
              
              <p className="mb-4 text-sm text-gray-600">
                Select which calendar view to show as the default home screen when users first log in.
              </p>
              
              <div className="max-w-md">
                <label htmlFor="home-screen" className="block text-base font-medium text-gray-800 mb-2">
                  Default Home Screen View
                </label>
                
                {/* Custom Dropdown */}
                <div className="relative mt-1">
                  <button
                    type="button"
                    className="relative w-full bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-3 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    aria-haspopup="listbox"
                    aria-expanded="true"
                    aria-labelledby="home-screen-label"
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={isLoading}
                  >
                    <span className="flex items-center">
                      {tempHomeScreen === 'location_calendar' ? (
                        <>
                          <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                          <span className="block truncate font-medium">Location Calendar</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                          <span className="block truncate font-medium">Mobile Calendar</span>
                        </>
                      )}
                    </span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </button>

                  {isOpen && (
                    <div 
                      className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      tabIndex={-1}
                      role="listbox"
                      aria-labelledby="home-screen-label"
                      aria-activedescendant={`home-screen-option-${tempHomeScreen}`}
                    >
                      <div 
                        className={`${
                          tempHomeScreen === 'location_calendar' ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                        } cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-50`}
                        id="home-screen-option-location_calendar"
                        role="option"
                        aria-selected={tempHomeScreen === 'location_calendar'}
                        onClick={() => handleSelectOption('location_calendar')}
                      >
                        <div className="flex items-center">
                          <Building2 className="h-5 w-5 text-blue-600 mr-2" />
                          <span className={`${
                            tempHomeScreen === 'location_calendar' ? 'font-semibold' : 'font-normal'
                          } block truncate`}>
                            Location Calendar
                          </span>
                        </div>
                        {tempHomeScreen === 'location_calendar' && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <p className="text-xs mt-1 text-gray-500 pl-7">Calendar view optimized for physical business locations</p>
                      </div>

                      <div 
                        className={`${
                          tempHomeScreen === 'mobile_calendar' ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                        } cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-50`}
                        id="home-screen-option-mobile_calendar"
                        role="option"
                        aria-selected={tempHomeScreen === 'mobile_calendar'}
                        onClick={() => handleSelectOption('mobile_calendar')}
                      >
                        <div className="flex items-center">
                          <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                          <span className={`${
                            tempHomeScreen === 'mobile_calendar' ? 'font-semibold' : 'font-normal'
                          } block truncate`}>
                            Mobile Calendar
                          </span>
                        </div>
                        {tempHomeScreen === 'mobile_calendar' && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <p className="text-xs mt-1 text-gray-500 pl-7">Calendar view optimized for mobile service businesses</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="mt-3 text-sm text-gray-500">
                  This setting applies to all users and determines which calendar view is displayed first when logging in.
                </p>
                
                {tempHomeScreen !== homeScreen && (
                  <p className="mt-2 text-sm text-blue-600">
                    <b>Note:</b> Changes will take effect the next time you navigate to the home screen.
                  </p>
                )}
              </div>
            </div>
            
            {/* Disable Features Section */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center mb-4">
                <EyeOff className="h-5 w-5 text-gray-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Disable Features</h3>
              </div>
              
              <p className="mb-4 text-sm text-gray-600">
                Choose which pages and features to disable for all users in your business. 
                Disabled pages will be completely hidden from the navigation menu.
              </p>
              
              <div className="mt-4 space-y-6">
                {/* Top Navigation Pages */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Top Navigation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {navigationPages.topNav.map(page => {
                      // Check if this page corresponds to the current home screen
                      const isHomeScreen = 
                        (tempHomeScreen === 'location_calendar' && page.id === 'calendarV2') || 
                        (tempHomeScreen === 'mobile_calendar' && page.id === 'mobile-calendar');
                      
                      return (
                        <label 
                          key={page.id} 
                          className={`flex items-center space-x-3 p-3 ${
                            isHomeScreen 
                              ? 'bg-gray-100 cursor-not-allowed' 
                              : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                          } rounded-md`}
                          title={isHomeScreen ? "Cannot disable the current home screen" : ""}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={tempDisabledPages.includes(page.id as PageId)}
                            onChange={() => handleTogglePage(page.id as PageId)}
                            disabled={isHomeScreen}
                          />
                          <span className={`${isHomeScreen ? 'text-gray-400' : 'text-gray-700'}`}>
                            {page.label}
                            {isHomeScreen && (
                              <span className="ml-2 text-xs text-gray-400">(Current home screen)</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                
                {/* Side Navigation Pages */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Side Navigation</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {navigationPages.sideNav.map(page => (
                      <label key={page.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={tempDisabledPages.includes(page.id as PageId)}
                          onChange={() => handleTogglePage(page.id as PageId)}
                        />
                        <span className="text-gray-700">{page.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Settings Pages */}
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Settings Pages</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {navigationPages.settingsPages.map(page => {
                      // Prevent disabling the current Application Settings page
                      const isCurrentPage = page.id === 'application-settings';
                      
                      return (
                        <label 
                          key={page.id} 
                          className={`flex items-center space-x-3 p-3 ${
                            isCurrentPage 
                              ? 'bg-gray-100 cursor-not-allowed' 
                              : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'
                          } rounded-md`}
                          title={isCurrentPage ? "Cannot disable the current page" : ""}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={tempDisabledPages.includes(page.id as PageId)}
                            onChange={() => handleTogglePage(page.id as PageId)}
                            disabled={isCurrentPage}
                          />
                          <span className={`${isCurrentPage ? 'text-gray-400' : 'text-gray-700'}`}>
                            {page.label}
                            {isCurrentPage && (
                              <span className="ml-2 text-xs text-gray-400">(Current page)</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <p className="text-sm text-gray-500">
                  <b>Note:</b> Disabling a page will make it inaccessible to all users. 
                  Changes will take effect immediately after saving.
                  The page set as home screen cannot be disabled.
                </p>
                
                {tempDisabledPages.length > 0 && (
                  <p className="mt-2 text-sm text-amber-600">
                    <b>Warning:</b> You have selected to disable {tempDisabledPages.length} page(s). 
                    These pages will not be accessible to any users.
                  </p>
                )}
              </div>
            </div>
            
            {/* Feedback Settings Section */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <MessageSquare className="h-5 w-5 text-gray-500 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Feedback Settings</h3>
                </div>
                <button
                  onClick={() => setIsCustomizeFeedbackModalOpen(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Customize Feedback Form
                </button>
              </div>
              
              <p className="mb-4 text-sm text-gray-600">
                Configure customer feedback collection settings and customize your feedback form.
              </p>
              
              {isLoadingFeedbackSettings ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${tempFeedbackEnabled ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        <MessageSquare className={`h-5 w-5 ${tempFeedbackEnabled ? 'text-green-600' : 'text-yellow-600'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${
                          tempFeedbackEnabled ? 'text-green-800' : 'text-yellow-800'
                        }`}>
                          {tempFeedbackEnabled ? 'Feedback system active' : 'Feedback Collection Disabled'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setTempFeedbackEnabled(!tempFeedbackEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        tempFeedbackEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          tempFeedbackEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Form Configuration Info */}
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="flex items-center">
                      <MessageSquare className="h-5 w-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Current Form Configuration</p>
                        <p className="text-sm text-gray-500">Click "Customize Feedback Form" to modify your feedback questions and settings.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveSettings}
                disabled={isLoading || isSaving || !hasChanges()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {!hasChanges() ? 'No Changes' : 'Save Settings'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customize Feedback Form Modal */}
      <CustomizeFeedbackFormModal
        isOpen={isCustomizeFeedbackModalOpen}
        onClose={() => setIsCustomizeFeedbackModalOpen(false)}
      />
    </div>
  );
} 
